import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Phone } from "lucide-react";
import { STORE } from "@/data/store";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/products", label: "Products", testid: "nav-link-products" },
  { to: "/about", label: "About Us", testid: "nav-link-about" },
  { to: "/contact", label: "Contact Us", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("arh-theme") === "dark";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("arh-theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-background/85 border-b border-border/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo-link" className="flex flex-col leading-none group">
          <span className="font-display text-xl sm:text-2xl font-bold tracking-tight group-hover:text-terra transition-colors duration-300">
            {STORE.name}
          </span>
          <span className="font-code text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
            Kirana &amp; General Store · Est. {STORE.est}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `link-underline font-sub text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "active text-terra" : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={`tel:+${STORE.phoneRaw}`}
            data-testid="header-phone-badge"
            className="hidden md:flex items-center gap-2 rounded-full border border-border px-4 py-2 font-code text-xs tracking-wider hover:border-terra hover:text-terra transition-colors duration-300"
          >
            <Phone size={13} strokeWidth={2.2} />
            {STORE.phoneLocal}
          </a>
          <button
            type="button"
            data-testid="theme-toggle-button"
            onClick={() => setDark((d) => !d)}
            aria-label="Toggle dark mode"
            className="h-10 w-10 rounded-full border border-border flex items-center justify-center hover:border-terra hover:text-terra transition-colors duration-300"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            className="lg:hidden h-10 w-10 rounded-full border border-border flex items-center justify-center"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-border/60 bg-background/95 backdrop-blur-md"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {[...LINKS, { to: "/privacy", label: "Privacy Policy", testid: "nav-link-privacy" }, { to: "/disclaimer", label: "Disclaimer", testid: "nav-link-disclaimer" }].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-display text-2xl ${isActive ? "text-terra" : "text-foreground"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={`tel:+${STORE.phoneRaw}`}
                data-testid="mobile-header-phone-link"
                className="font-code text-sm tracking-wider text-terra mt-2"
              >
                Call: {STORE.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
